'use strict';

// Port of recipe/src/Chewba/Utils/NutrientSanityValidator.php — emits flag keys
// only; human-readable copy lives in FoodReadyState.react.js (SANITY_EXPLANATIONS).

// ─── Tolerance constants ───
const ATWATER_THRESHOLD = 0.15;
const ATWATER_MIN_CAL_PER_GRAM = 1.0;
const ENERGY_KJ_PER_KCAL = 4.184;
const ENERGY_KJ_TOLERANCE = 0.01;
const MASS_BALANCE_TOL_FRAC = 0.03;
const TREE_SUM_TOL = 0.03;
const FIBER_SPLIT_TOL = 0.10;
const FAT_TREE_MIN_RATIO = 0.85;
const AMINO_MIN_RATIO = 0.70;
const FAT_TREE_MIN_PARENT_G = 0.5;
const VIT_A_TOL = 0.20;
const VIT_D_IU_PER_MCG = 40.0;
const VIT_D_TOL = 0.05;
const FLUIDS_WATER_TOL = 0.20;
const CALORIES_MAX = 5000;
const SODIUM_MAX = 10000;
const CAL_PER_GRAM_MAX = 9.5;
const FRU_PER_GRAM_MAX = 0.2;
const VEG_PER_GRAM_MAX = 0.2;
const BOUND_G_PER_G = 1.0;
const BOUND_MG_PER_G = 1000.0;
const BOUND_UG_PER_G = 1000000.0;

// ─── nutrNo groups ───
const INDIVIDUAL_SFAS = [
    '607', '608', '609', '610', '611', '612', '613', '614', '615',
    '624', '652', '653', '654', '696',
];
const INDIVIDUAL_MUFAS = [
    '617',
    '625', '626', '628', '630',
    '671',
    '687', '697',
];
const INDIVIDUAL_PUFAS = [
    '618', '619', '620',
    '621', '629', '631',
    '627',
    '670',
    '672', '685',
    '689', '853',
];
const OMEGA3_SPECIES = ['621', '629', '631', '627', '851', '852'];
const OMEGA6_SPECIES = ['672', '675', '685', '853', '855'];
const TRANS_COMPONENTS = ['693', '695'];
const AMINO_ACIDS = [
    '501', '502', '503', '504', '505', '506', '507', '508', '509',
    '510', '511', '512', '513', '514', '515', '516', '517', '518',
];
const SUGAR_COMPONENTS = ['210', '211', '212', '213', '214', '287'];
const TOCOPHEROL_TOCOTRIENOL = ['341', '342', '343', '344', '345', '346', '347'];
const MINERALS_MG = ['301', '303', '304', '305', '306', '307', '309', '312', '315'];
const MINERALS_UG = ['317', 'IOD', 'MBD', 'CHR'];

const G_UNIT_NUTRIENTS = [
    '203', '204', '205', '207', '209', '210', '211', '212', '213', '214',
    '221', '255', '257', '269', '287', '291', '605', '606', '645', '646',
    'ASG', 'SAL', 'SFB', 'IFB',
];

const ALLOW_NEGATIVE = ['RAL'];

// ─── Flag keys ───
export const KEY_CALORIES = 'implausible calories';
export const KEY_SODIUM = 'implausible sodium';
export const KEY_MACROS = 'implausible macros';
export const KEY_CALORIE_DENSITY = 'implausible calorie density';
export const KEY_FRUIT_DENSITY = 'implausible fruit density';
export const KEY_VEGGIE_DENSITY = 'implausible veggie density';

export const KEY_ENERGY_KJ = 'energy kJ mismatch';
export const KEY_MASS_BALANCE = 'mass balance off';
export const KEY_NEGATIVE = 'negative nutrient';
export const KEY_OVERWEIGHT = 'nutrient exceeds weight';
export const KEY_UNIT_OUTLIER = 'unit outlier';

export const KEY_CARB_TREE = 'carb tree inconsistent';
export const KEY_SUGAR_COMPOSITION = 'sugar composition inconsistent';
export const KEY_FIBER_SPLIT = 'fiber split inconsistent';
export const KEY_FAT_TREE = 'fat tree inconsistent';
export const KEY_SFA_SUM = 'saturated FA sum inconsistent';
export const KEY_MUFA_SUM = 'MUFA sum inconsistent';
export const KEY_PUFA_SUM = 'PUFA sum inconsistent';
export const KEY_TRANS_SUM = 'trans FA sum inconsistent';
export const KEY_OMEGA_CONSISTENCY = 'omega inconsistent';
export const KEY_AMINO_ACIDS = 'amino acids inconsistent';
export const KEY_STEROL_SUM = 'sterol sum inconsistent';
export const KEY_FODMAP = 'fodmap inconsistent';
export const KEY_VIT_E_TREE = 'vitamin E tree inconsistent';
export const KEY_FLUIDS_WATER = 'fluids water mismatch';
export const KEY_SERVINGS_HIERARCHY = 'servings hierarchy inconsistent';
export const KEY_MINERALS_ASH = 'minerals exceed ash';

export const KEY_VIT_D_UNITS = 'vitamin D unit mismatch';
export const KEY_VIT_D_SPLIT = 'vitamin D split inconsistent';
export const KEY_VIT_A_UNITS = 'vitamin A unit mismatch';
export const KEY_FOLATE = 'folate inconsistent';

// ─── Shared primitives ───

function v(values, code) {
    return Number(values[code] ?? 0);
}

function sumCodes(values, codes) {
    let sum = 0.0;
    for (const c of codes) {
        sum += v(values, c);
    }
    return sum;
}

function anyPresent(values, codes) {
    for (const c of codes) {
        if ((Number(values[c] ?? 0)) > 0) {
            return true;
        }
    }
    return false;
}

// ─── Density & absolute caps ───

function checkDensities(values, gps, flags) {
    const cals = v(values, '208');
    const fru = v(values, 'FRU');
    const veg = v(values, 'VEG');

    if (cals / gps > CAL_PER_GRAM_MAX) {
        flags.add(KEY_CALORIE_DENSITY);
    }
    if (fru / gps > FRU_PER_GRAM_MAX) {
        flags.add(KEY_FRUIT_DENSITY);
    }
    if (veg / gps > VEG_PER_GRAM_MAX) {
        flags.add(KEY_VEGGIE_DENSITY);
    }
}

// ─── Tier 1 ───

function checkAtwater(values, refMass) {
    const cals = v(values, '208');
    const minCals = refMass !== null ? ATWATER_MIN_CAL_PER_GRAM * refMass : 50.0;
    if (cals <= minCals) {
        return null;
    }
    const implied = 4 * v(values, '203')
        + 9 * v(values, '204')
        + 4 * v(values, '205')
        + 7 * v(values, '221');
    if (implied <= 0) {
        return null;
    }
    if (Math.abs(implied - cals) / cals > ATWATER_THRESHOLD) {
        return KEY_MACROS;
    }
    return null;
}

function checkEnergyKj(values) {
    const cals = v(values, '208');
    const kj = v(values, '268');
    if (cals <= 0 || kj <= 0) {
        return null;
    }
    const expected = cals * ENERGY_KJ_PER_KCAL;
    if (Math.abs(kj - expected) / expected > ENERGY_KJ_TOLERANCE) {
        return KEY_ENERGY_KJ;
    }
    return null;
}

function checkNegative(values) {
    for (const code of Object.keys(values)) {
        if (ALLOW_NEGATIVE.includes(String(code))) {
            continue;
        }
        if (Number(values[code]) < 0) {
            return KEY_NEGATIVE;
        }
    }
    return null;
}

function checkOverweight(values, refMass) {
    if (refMass === null || refMass <= 0) {
        return null;
    }
    for (const code of G_UNIT_NUTRIENTS) {
        if (v(values, code) > refMass) {
            return KEY_OVERWEIGHT;
        }
    }
    return null;
}

function checkUnitOutlier(values, refMass) {
    if (refMass === null || refMass <= 0) {
        return null;
    }
    const gCap = BOUND_G_PER_G * refMass;
    const mgCap = BOUND_MG_PER_G * refMass;
    const ugCap = BOUND_UG_PER_G * refMass;

    for (const code of G_UNIT_NUTRIENTS) {
        if (v(values, code) > gCap) {
            return KEY_UNIT_OUTLIER;
        }
    }
    for (const code of MINERALS_MG) {
        if (v(values, code) > mgCap) {
            return KEY_UNIT_OUTLIER;
        }
    }
    for (const code of MINERALS_UG) {
        if (v(values, code) > ugCap) {
            return KEY_UNIT_OUTLIER;
        }
    }
    return null;
}

function checkMassBalance(values, refMass) {
    if (refMass === null || refMass <= 0) {
        return null;
    }
    if (v(values, '255') <= 0 && v(values, '207') <= 0) {
        return null;
    }
    const sum = v(values, '203')
        + v(values, '204')
        + v(values, '205')
        + v(values, '255')
        + v(values, '207')
        + v(values, '221');
    const tol = refMass * MASS_BALANCE_TOL_FRAC;
    if (sum > refMass + tol || sum < refMass - tol) {
        return KEY_MASS_BALANCE;
    }
    return null;
}

// ─── Tier 2 ───

function checkCarbTree(values) {
    const carbs = v(values, '205');
    if (carbs <= 0) {
        return null;
    }
    const sum = v(values, '269') + v(values, '209') + v(values, '291');
    if (sum > carbs * (1 + TREE_SUM_TOL)) {
        return KEY_CARB_TREE;
    }
    return null;
}

function checkSugarComposition(values) {
    const sugars = v(values, '269');
    if (sugars <= 0) {
        return null;
    }
    const components = sumCodes(values, SUGAR_COMPONENTS);
    if (components > sugars * (1 + TREE_SUM_TOL)) {
        return KEY_SUGAR_COMPOSITION;
    }
    if (v(values, 'ASG') > sugars * (1 + TREE_SUM_TOL)) {
        return KEY_SUGAR_COMPOSITION;
    }
    return null;
}

function checkFiberSplit(values) {
    const fiber = v(values, '291');
    const soluble = v(values, 'SFB');
    const insoluble = v(values, 'IFB');
    if (fiber <= 0 || (soluble <= 0 && insoluble <= 0)) {
        return null;
    }
    const sum = soluble + insoluble;
    if (Math.abs(sum - fiber) / fiber > FIBER_SPLIT_TOL) {
        return KEY_FIBER_SPLIT;
    }
    return null;
}

function checkFatTree(values) {
    const fat = v(values, '204');
    if (fat <= 0) {
        return null;
    }
    const sat = v(values, '606');
    const mono = v(values, '645');
    const poly = v(values, '646');
    const trans = v(values, '605');
    const sum = sat + mono + poly + trans;

    if (sum > fat * (1 + TREE_SUM_TOL)) {
        return KEY_FAT_TREE;
    }
    if (fat >= FAT_TREE_MIN_PARENT_G
        && sat > 0 && mono > 0 && poly > 0
        && sum < fat * FAT_TREE_MIN_RATIO) {
        return KEY_FAT_TREE;
    }
    return null;
}

function checkFatSubSum(values, parentCode, components, key) {
    const parent = v(values, parentCode);
    if (parent <= 0) {
        return null;
    }
    if (sumCodes(values, components) > parent * (1 + TREE_SUM_TOL)) {
        return key;
    }
    return null;
}

function checkTransSum(values) {
    const trans = v(values, '605');
    if (trans <= 0) {
        return null;
    }
    if (sumCodes(values, TRANS_COMPONENTS) > trans * (1 + TREE_SUM_TOL)) {
        return KEY_TRANS_SUM;
    }
    return null;
}

function checkOmegaConsistency(values) {
    const dha = v(values, '621');
    const epa = v(values, '629');
    const ala = v(values, '851');
    const alaUndiff = v(values, '619');
    const om3 = v(values, 'OM3');
    const oma = v(values, 'OMA');
    const poly = v(values, '646');

    if (om3 > 0 && (dha > 0 || epa > 0)) {
        const expected = dha + epa;
        if (Math.abs(om3 - expected) > 0.001 + expected * TREE_SUM_TOL) {
            return KEY_OMEGA_CONSISTENCY;
        }
    }
    if (oma > 0 && (dha > 0 || epa > 0 || ala > 0)) {
        const expected = dha + epa + ala;
        if (Math.abs(oma - expected) > 0.001 + expected * TREE_SUM_TOL) {
            return KEY_OMEGA_CONSISTENCY;
        }
    }
    if (ala > 0 && alaUndiff > 0) {
        const diff = Math.abs(ala - alaUndiff) / Math.max(ala, alaUndiff);
        if (diff > TREE_SUM_TOL) {
            return KEY_OMEGA_CONSISTENCY;
        }
    }
    if (poly > 0) {
        if (sumCodes(values, OMEGA3_SPECIES) > poly * (1 + TREE_SUM_TOL)) {
            return KEY_OMEGA_CONSISTENCY;
        }
        if (sumCodes(values, OMEGA6_SPECIES) > poly * (1 + TREE_SUM_TOL)) {
            return KEY_OMEGA_CONSISTENCY;
        }
    }
    return null;
}

function checkAminoAcids(values) {
    const protein = v(values, '203');
    if (protein <= 0) {
        return null;
    }
    const adjusted = v(values, '257');
    if (adjusted > protein * (1 + TREE_SUM_TOL)) {
        return KEY_AMINO_ACIDS;
    }
    if (!anyPresent(values, AMINO_ACIDS)) {
        return null;
    }
    const sum = sumCodes(values, AMINO_ACIDS);
    if (sum > protein * (1 + TREE_SUM_TOL)) {
        return KEY_AMINO_ACIDS;
    }
    let allPresent = true;
    for (const code of AMINO_ACIDS) {
        if ((Number(values[code] ?? 0)) <= 0) {
            allPresent = false;
            break;
        }
    }
    if (allPresent && sum < protein * AMINO_MIN_RATIO) {
        return KEY_AMINO_ACIDS;
    }
    return null;
}

function checkSterolSum(values) {
    const phyto = v(values, '636');
    if (phyto <= 0) {
        return null;
    }
    const sum = v(values, '638') + v(values, '639') + v(values, '641');
    if (sum > phyto * (1 + TREE_SUM_TOL)) {
        return KEY_STEROL_SUM;
    }
    return null;
}

function checkFodmap(values) {
    const polyols = v(values, 'PLY');
    if (polyols > 0) {
        const sorMan = v(values, 'SOR') + v(values, 'MAN');
        if (sorMan > polyols * (1 + TREE_SUM_TOL)) {
            return KEY_FODMAP;
        }
    }
    const fructoseTotal = v(values, '212');
    const fodmapFru = v(values, 'FRC');
    if (fructoseTotal > 0 && fodmapFru > fructoseTotal * (1 + TREE_SUM_TOL)) {
        return KEY_FODMAP;
    }
    const lactoseTotal = v(values, '213');
    const fodmapLac = v(values, 'LAC');
    if (lactoseTotal > 0 && fodmapLac > lactoseTotal * (1 + TREE_SUM_TOL)) {
        return KEY_FODMAP;
    }
    return null;
}

function checkVitETree(values) {
    const total = v(values, '323');
    if (total <= 0) {
        return null;
    }
    if (v(values, '573') > total * (1 + TREE_SUM_TOL)) {
        return KEY_VIT_E_TREE;
    }
    if (sumCodes(values, TOCOPHEROL_TOCOTRIENOL) > total * (1 + TREE_SUM_TOL)) {
        return KEY_VIT_E_TREE;
    }
    return null;
}

function checkFluidsWater(values) {
    const fluids = v(values, 'FLU');
    const water = v(values, '255');
    if (fluids <= 0 || water <= 0) {
        return null;
    }
    if (Math.abs(fluids - water) / Math.max(fluids, water) > FLUIDS_WATER_TOL) {
        return KEY_FLUIDS_WATER;
    }
    return null;
}

function checkServingsHierarchy(values) {
    const veg = v(values, 'VEG');
    const lfy = v(values, 'LFY');
    const svg = v(values, 'SVG');
    const fru = v(values, 'FRU');
    const ber = v(values, 'BER');

    if (veg > 0 && lfy > veg * (1 + TREE_SUM_TOL)) {
        return KEY_SERVINGS_HIERARCHY;
    }
    if (veg > 0 && svg > veg * (1 + TREE_SUM_TOL)) {
        return KEY_SERVINGS_HIERARCHY;
    }
    if (fru > 0 && ber > fru * (1 + TREE_SUM_TOL)) {
        return KEY_SERVINGS_HIERARCHY;
    }
    return null;
}

function checkMineralsAsh(values) {
    const ash = v(values, '207');
    if (ash <= 0) {
        return null;
    }
    let mineralsMg = 0.0;
    for (const code of MINERALS_MG) {
        mineralsMg += v(values, code);
    }
    for (const code of MINERALS_UG) {
        mineralsMg += v(values, code) / 1000.0;
    }
    const ashMg = ash * 1000.0;
    if (mineralsMg > ashMg * (1 + TREE_SUM_TOL)) {
        return KEY_MINERALS_ASH;
    }
    return null;
}

// ─── Tier 3: conversion equalities ───

function checkVitDUnits(values) {
    const iu = v(values, '324');
    const mcg = v(values, '328');
    if (iu <= 0 || mcg <= 0) {
        return null;
    }
    const expected = mcg * VIT_D_IU_PER_MCG;
    if (Math.abs(iu - expected) / expected > VIT_D_TOL) {
        return KEY_VIT_D_UNITS;
    }
    return null;
}

function checkVitDSplit(values) {
    const d2 = v(values, '325');
    const d3 = v(values, '326');
    const total = v(values, '328');
    if (total <= 0 || (d2 <= 0 && d3 <= 0)) {
        return null;
    }
    const sum = d2 + d3;
    if (Math.abs(sum - total) / total > TREE_SUM_TOL) {
        return KEY_VIT_D_SPLIT;
    }
    return null;
}

function checkVitAUnits(values) {
    const rae = v(values, '320');
    const iu = v(values, '318');
    const retinol = v(values, '319');
    const betaCar = v(values, '321');
    const alphaCar = v(values, '322');
    const betaCryp = v(values, '334');

    if (rae <= 0 && iu <= 0) {
        return null;
    }
    if (retinol <= 0 && betaCar <= 0 && alphaCar <= 0 && betaCryp <= 0) {
        return null;
    }

    const raeCalc = retinol + betaCar / 12.0 + alphaCar / 24.0 + betaCryp / 24.0;
    const iuCalc = retinol * 3.33 + betaCar * 1.67 + (alphaCar + betaCryp) * 0.83;

    if (rae > 0 && raeCalc > 0) {
        if (Math.abs(rae - raeCalc) / Math.max(rae, raeCalc) > VIT_A_TOL) {
            return KEY_VIT_A_UNITS;
        }
    }
    if (iu > 0 && iuCalc > 0) {
        if (Math.abs(iu - iuCalc) / Math.max(iu, iuCalc) > VIT_A_TOL) {
            return KEY_VIT_A_UNITS;
        }
    }
    return null;
}

function checkFolate(values) {
    const folicAcid = v(values, '431');
    const dfe = v(values, '435');
    if (folicAcid > 0 && dfe > 0 && folicAcid > dfe * (1 + TREE_SUM_TOL)) {
        return KEY_FOLATE;
    }
    return null;
}

function runRules(values, refMass, flags) {
    const checks = [
        checkAtwater(values, refMass),
        checkEnergyKj(values),
        checkNegative(values),
        checkOverweight(values, refMass),
        checkUnitOutlier(values, refMass),
        checkMassBalance(values, refMass),
        checkCarbTree(values),
        checkSugarComposition(values),
        checkFiberSplit(values),
        checkFatTree(values),
        checkFatSubSum(values, '606', INDIVIDUAL_SFAS, KEY_SFA_SUM),
        checkFatSubSum(values, '645', INDIVIDUAL_MUFAS, KEY_MUFA_SUM),
        checkFatSubSum(values, '646', INDIVIDUAL_PUFAS, KEY_PUFA_SUM),
        checkTransSum(values),
        checkOmegaConsistency(values),
        checkAminoAcids(values),
        checkSterolSum(values),
        checkFodmap(values),
        checkVitETree(values),
        checkFluidsWater(values),
        checkServingsHierarchy(values),
        checkMineralsAsh(values),
        checkVitDUnits(values),
        checkVitDSplit(values),
        checkVitAUnits(values),
        checkFolate(values),
    ];
    for (const key of checks) {
        if (key !== null) {
            flags.add(key);
        }
    }
}

export function validateNutrientSanity(food) {
    const values = food?.nutrients?.values ?? {};
    const gps = Number(food?.grams_per_serving ?? 0);
    const cals = v(values, '208');
    const sodium = v(values, '307');

    const flags = new Set();

    if (cals > CALORIES_MAX) {
        flags.add(KEY_CALORIES);
    }
    if (sodium > SODIUM_MAX) {
        flags.add(KEY_SODIUM);
    }
    if (gps > 0) {
        checkDensities(values, gps, flags);
    }

    runRules(values, gps > 0 ? gps : null, flags);

    return Array.from(flags);
}
